import type { SpecType } from '@local/lib';

import { Badge, Button, Combobox, ScopedNotification, Spinner, Tooltip } from '@salesforce/design-system-react';
import { useState } from 'react';

import type { QualityAdjustmentTemplate } from '../templateTypes';

import { PageMeta, QualityAdjustmentNavigation } from '../../../shared/components';
import { IconCategory } from '../../../shared/types/Enum';
import { showErrorNotification, showSuccessNotification } from '../../../shared/utils/notifications';
import { FieldLabelRow } from '../../quality-formulas/components/FieldLabelRow';
import { getFormulaAdjustmentCategory, SPEC_TYPES } from '../../quality-formulas/qualityFormulaMetadata';
import '../../quality-formulas/components/qualitySpecsWorkbench.css';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { TemplateEditorContent } from '../components/TemplateEditorModal';
import { usePermissions } from '../hooks/usePermissions';
import { useTemplateActions, useTemplates } from '../hooks/useTemplates';
import { ADJUSTMENT_CATEGORY_LABEL } from '../templateTypes';

const ALL_SPECS_FILTER_VALUE = 'ALL';
const PAGE_SIZE = 20;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const getSpecLabel = (key: SpecType): string => SPEC_TYPES.find(spec => spec.key === key)?.label ?? key;

interface TemplateRowProps {
  canManage: boolean;
  onDelete: (template: QualityAdjustmentTemplate) => void;
  onDuplicate: (template: QualityAdjustmentTemplate) => void;
  onEdit: (template: QualityAdjustmentTemplate) => void;
  template: QualityAdjustmentTemplate;
}

const TemplateRow: React.FC<TemplateRowProps> = ({ canManage, onDelete, onDuplicate, onEdit, template }) => {
  const adjustmentCategory = getFormulaAdjustmentCategory(template.config.formulaType);

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)' }}>
          <span className="slds-text-body_regular" style={{ fontWeight: 'var(--slds-g-font-weight-7)' }}>
            {template.name}
          </span>
          {template.description ? (
            <span className="slds-text-body_small slds-text-color_weak slds-truncate" style={{ display: 'block' }}>
              {template.description}
            </span>
          ) : null}
        </div>
      </td>
      <td>
        <Badge color="light" content={getSpecLabel(template.specType)} />
      </td>
      <td>
        <span className="slds-text-body_small">{ADJUSTMENT_CATEGORY_LABEL[adjustmentCategory]}</span>
      </td>
      <td>
        <Badge color="light" content={template.config.formulaType} />
      </td>
      <td>
        <span className="slds-text-body_small slds-text-color_weak">{formatDate(template.updatedAt)}</span>
      </td>
      <td className="slds-text-align_center">
        <div
          className="slds-grid slds-grid_align-end slds-grid_vertical-align-center slds-nowrap"
          style={{ gap: 'var(--slds-g-spacing-1)' }}
        >
          <Tooltip align="right" content={canManage ? 'Edit template' : 'View template'}>
            <Button
              assistiveText={{ icon: canManage ? 'Edit template' : 'View template' }}
              iconCategory={IconCategory.Utility}
              iconName={canManage ? 'edit' : 'preview'}
              onClick={() => {
                onEdit(template);
              }}
              variant="icon"
            />
          </Tooltip>
          {canManage ? (
            <>
              <Tooltip align="right" content="Duplicate template">
                <Button
                  assistiveText={{ icon: 'Duplicate template' }}
                  iconCategory={IconCategory.Utility}
                  iconName="copy"
                  onClick={() => {
                    onDuplicate(template);
                  }}
                  variant="icon"
                />
              </Tooltip>
              {/* `slds-button_icon-error` is a genuine SLDS button-icon variant (not a custom
                  color) — deleting a template is destructive, and this is the library's own hook
                  for that, already red in every state including hover/focus (see SpecNavigator's
                  own remove-spec button for the same pattern). */}
              <Tooltip align="right" content="Delete template">
                <Button
                  assistiveText={{ icon: 'Delete template' }}
                  className="slds-button_icon-error"
                  iconCategory={IconCategory.Utility}
                  iconName="delete"
                  onClick={() => {
                    onDelete(template);
                  }}
                  variant="icon"
                />
              </Tooltip>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

interface TemplateListContentProps {
  canManageTemplates: boolean;
  currentPage: number;
  isLoading: boolean;
  onDelete: (template: QualityAdjustmentTemplate) => void;
  onDuplicate: (template: QualityAdjustmentTemplate) => void;
  onEdit: (template: QualityAdjustmentTemplate) => void;
  onPageChange: (page: number) => void;
  selectedSpecType: null | SpecType;
  templates: QualityAdjustmentTemplate[] | undefined;
}

// Extracted so the loading/empty/table-of-results states are each a single, non-nested condition
// instead of a chained ternary.
const TemplateListContent: React.FC<TemplateListContentProps> = ({
  canManageTemplates,
  currentPage,
  isLoading,
  onDelete,
  onDuplicate,
  onEdit,
  onPageChange,
  selectedSpecType,
  templates,
}) => {
  if (isLoading) {
    return (
      <div className="slds-grid slds-grid_align-center slds-p-around_x-large">
        <Spinner hasContainer={false} />
      </div>
    );
  }

  const visibleTemplates = (templates ?? []).filter(
    template => selectedSpecType === null || template.specType === selectedSpecType,
  );

  if (visibleTemplates.length === 0) {
    return (
      // `borderStyle: 'dashed'` is the one hardcoded value here — `slds-box` has no dashed-border
      // modifier of its own, so this overrides just that one property while still inheriting the
      // box's real `--slds-g-color-border-base-1` border color from the class.
      <div className="slds-box slds-text-align_center" style={{ borderStyle: 'dashed' }}>
        <p className="slds-text-body_small slds-text-color_weak">
          {selectedSpecType === null ? 'No templates yet.' : `No ${getSpecLabel(selectedSpecType)} templates yet.`}{' '}
          {canManageTemplates ? 'Click "Create Template" to build one.' : 'Check back later.'}
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(visibleTemplates.length / PAGE_SIZE);
  const paginatedTemplates = visibleTemplates.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-3)' }}>
      <div className="slds-scrollable_x">
        <table className="slds-table slds-table_bordered slds-table_cell-buffer">
          <thead>
            <tr className="slds-line-height_reset">
              <th scope="col">
                <div className="slds-truncate">Template Name</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Quality Specification</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Adjustment Type</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Formula Type</div>
              </th>
              <th scope="col">
                <div className="slds-truncate">Last Modified</div>
              </th>
              <th className="slds-text-align_center" scope="col">
                <div className="slds-truncate">Action</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedTemplates.map(template => (
              <TemplateRow
                canManage={canManageTemplates}
                key={template.id}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onEdit={onEdit}
                template={template}
              />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div
          className="slds-grid slds-grid_align-center slds-grid_vertical-align-center"
          style={{ gap: 'var(--slds-g-spacing-2)' }}
        >
          <Button
            assistiveText={{ icon: 'Previous page' }}
            disabled={currentPage === 1}
            iconCategory={IconCategory.Utility}
            iconName="chevronleft"
            onClick={() => {
              onPageChange(currentPage - 1);
            }}
            variant="icon"
          />
          <span className="slds-text-body_regular slds-text-color_weak">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            assistiveText={{ icon: 'Next page' }}
            disabled={currentPage === totalPages}
            iconCategory={IconCategory.Utility}
            iconName="chevronright"
            onClick={() => {
              onPageChange(currentPage + 1);
            }}
            variant="icon"
          />
        </div>
      ) : null}
    </div>
  );
};

/**
 * The Template Manager screen — a dedicated home for managing reusable Quality Adjustment
 * Templates, kept intentionally separate from Contract Entry (`QualityFormulasPage.tsx`) so
 * editing a contract can never accidentally change a shared template (see `QualityAdjustmentNavigation`
 * for how users switch between the two).
 *
 * Every template belongs to exactly one Quality Specification (see `templateTypes.ts`), so the
 * spec filter below is generated straight from `SPEC_TYPES` — adding a new spec there is all a new
 * Quality Specification needs to show up here too.
 */
export const TemplateManagerPage: React.FC = () => {
  const { data: templates, isLoading } = useTemplates();
  const { duplicateTemplate, removeTemplate } = useTemplateActions();
  const { canManageTemplates } = usePermissions();
  const [pendingDelete, setPendingDelete] = useState<null | QualityAdjustmentTemplate>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [specFilter, setSpecFilter] = useState<string>(ALL_SPECS_FILTER_VALUE);
  const [currentPage, setCurrentPage] = useState(1);
  // Create/Edit swap `TemplateEditorContent` in for the filter/list below instead of navigating to
  // the standalone `/templates/new`/`/templates/:id/edit` routes or opening a floating dialog —
  // same inline pattern as the Quality Adjustment workbench's `TemplateManagerPanel`.
  const [editorState, setEditorState] = useState<{ isOpen: boolean; templateId: null | string }>({
    isOpen: false,
    templateId: null,
  });

  const openEditor = (templateId: null | string) => {
    setEditorState({ isOpen: true, templateId });
  };
  const closeEditor = () => {
    setEditorState({ isOpen: false, templateId: null });
  };

  const selectedSpecType =
    specFilter === ALL_SPECS_FILTER_VALUE
      ? null
      : // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
        (specFilter as SpecType);
  const specFilterOptions = [
    { id: ALL_SPECS_FILTER_VALUE, label: 'All Quality Specifications' },
    ...SPEC_TYPES.map(spec => ({ id: spec.key, label: spec.label })),
  ];
  const selectedSpecFilterOption = specFilterOptions.find(option => option.id === specFilter);

  const handleDuplicate = async (template: QualityAdjustmentTemplate) => {
    try {
      const duplicate = await duplicateTemplate(template.id);
      showSuccessNotification(`Duplicated "${template.name}".`);
      openEditor(duplicate.id);
    } catch (error) {
      showErrorNotification(error, 'Unable to duplicate template');
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await removeTemplate(pendingDelete.id);
      showSuccessNotification(`Deleted "${pendingDelete.name}".`);
    } catch (error) {
      showErrorNotification(error, 'Unable to delete template');
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  return (
    <div className="slds-p-bottom_x-large slds-p-horizontal_medium slds-p-top_small" style={{ width: '100%' }}>
      <PageMeta
        description="Manage reusable, spec-scoped Quality Adjustment templates contracts can load as a starting point."
        title="Template Manager"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-4)' }}>
        <QualityAdjustmentNavigation />
        {editorState.isOpen ? (
          <TemplateEditorContent onClose={closeEditor} templateId={editorState.templateId} />
        ) : (
          <>
            <div
              className="slds-grid slds-grid_align-spread slds-grid_vertical-align-start slds-wrap"
              style={{ gap: 'var(--slds-g-spacing-4)' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)', maxWidth: 640 }}>
                <h1 className="slds-text-heading_medium">Template Manager</h1>
                <span className="slds-text-body_small slds-text-color_weak">
                  Manage reusable, spec-scoped Quality Adjustment templates. Contract Entry can load a template's values
                  as a starting point — editing a contract never changes the template it came from.
                </span>
              </div>
              {canManageTemplates ? (
                <Button
                  iconCategory={IconCategory.Utility}
                  iconName="add"
                  iconPosition="left"
                  label="Create Template"
                  onClick={() => {
                    openEditor(null);
                  }}
                  variant="brand"
                />
              ) : null}
            </div>

            {canManageTemplates ? null : (
              <ScopedNotification theme="light">
                <p className="slds-text-body_small slds-text-color_weak">
                  You have read-only access to templates. Only Admin users can create, edit, delete, or duplicate them —
                  you can still load any template onto a contract from Contract Entry.
                </p>
              </ScopedNotification>
            )}

            <div style={{ maxWidth: 320 }}>
              <FieldLabelRow label="Filter by Quality Specification" />
              <Combobox
                assistiveText={{ label: 'Filter by Quality Specification' }}
                className="qfw-template-filter-combobox"
                classNameMenu="qfw-template-filter-menu"
                events={{
                  onSelect: (_event, data) => {
                    setSpecFilter(data.selection[0].id);
                    setCurrentPage(1);
                  },
                }}
                id="template-spec-filter"
                labels={{ placeholder: 'Select a Quality Specification' }}
                options={specFilterOptions}
                predefinedOptionsOnly
                selection={selectedSpecFilterOption ? [selectedSpecFilterOption] : []}
                variant="readonly"
              />
            </div>

            <TemplateListContent
              canManageTemplates={canManageTemplates}
              currentPage={currentPage}
              isLoading={isLoading}
              onDelete={setPendingDelete}
              onDuplicate={next => {
                void handleDuplicate(next);
              }}
              onEdit={template => {
                openEditor(template.id);
              }}
              onPageChange={setCurrentPage}
              selectedSpecType={selectedSpecType}
              templates={templates}
            />
          </>
        )}
      </div>

      <ConfirmationModal
        confirmLoading={isDeleting}
        confirmText="Delete"
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        opened={pendingDelete !== null}
        title="Delete template?"
      >
        {pendingDelete
          ? `Delete "${pendingDelete.name}"? Contracts that already loaded it keep their values — this can't be undone.`
          : ''}
      </ConfirmationModal>
    </div>
  );
};
